import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Button, Card, Col, Empty, Row, Space, Table, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ToolOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  DashboardPageShell,
  DashboardPanel,
  DashboardRefreshButton,
  DashboardSearchInput,
  DashboardStatusTag,
  DashboardFleetStatusFilterGrid,
  DashboardFleetStatusFilterButton,
  DashboardForkliftMobileCard,
  DashboardAccountMenu,
} from './components/dashboard';
import { api } from './api/http';
import { useAuth } from './auth/AuthProvider';
import { resolveActiveCustomerContext } from './auth/customerContext';
import './components/dashboard/dashboardFleet.css';
import './TechnicianDashboard.css';

const { Text, Title } = Typography;

const maintText = {
  OVERDUE: 'Overdue',
  DUE_SOON: 'Due soon',
  UPCOMING: 'Upcoming',
  ON_TRACK: 'On track',
};

const maintColor = {
  OVERDUE: 'red',
  DUE_SOON: 'orange',
  UPCOMING: 'blue',
  ON_TRACK: 'green',
};

const svcText = {
  IN_SERVICE: 'In service',
  DOWN: 'Down',
  WAITING_ON_PARTS: 'Waiting on parts',
};

const svcColor = {
  IN_SERVICE: 'blue',
  DOWN: 'red',
  WAITING_ON_PARTS: 'gold',
};

function safeMaintKey(v) {
  const k = String(v || '').trim().toUpperCase();
  return k === 'NEEDS_SETUP' ? 'ON_TRACK' : k;
}

export default function TechnicianDashboard() {
  const navigate = useNavigate();
  const { session } = useAuth();

  const userId = session?.userId ?? null;

  const [selectionReady, setSelectionReady] = useState(false);

  const customerId =
    session?.customerId ?? Number(localStorage.getItem('customerId') || 0);

  const [forklifts, setForklifts] = useState([]);
  const [maintSummary, setMaintSummary] = useState({
    overdueCount: 0,
    dueSoonCount: 0,
    upcomingCount: 0,
  });
  const [serviceSummary, setServiceSummary] = useState({
    inServiceCount: 0,
    downCount: 0,
    waitingPartsCount: 0,
  });
  const [settings, setSettings] = useState({
    serviceIntervalType: 'DAYS',
    serviceIntervalValue: 90,
    dueSoonWindow: 30,
    upcomingWindow: 60,
  });

  const [maintFilter, setMaintFilter] = useState('ALL');
  const [serviceFilter, setServiceFilter] = useState('ALL');
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);

  const readSelectedCustomerId = () => {
    try {
      const v = Number(localStorage.getItem('selectedCustomerId') || 0);
      return v > 0 ? v : null;
    } catch {
      return null;
    }
  };

  const setActiveCustomerStorage = (cid, cname) => {
    try {
      localStorage.setItem('customerId', String(cid));
      localStorage.setItem('selectedCustomerId', String(cid));
      localStorage.setItem('customerName', String(cname || ''));
      localStorage.removeItem('pendingCustomerId');
    } catch {}

    try {
      const raw = localStorage.getItem('session');
      const base = raw ? JSON.parse(raw) : {};
      base.customerId = cid;
      base.customerName = cname;
      delete base.pendingCustomerId;
      localStorage.setItem('session', JSON.stringify(base));
    } catch {
      try {
        localStorage.setItem(
          'session',
          JSON.stringify({ customerId: cid, customerName: cname })
        );
      } catch {}
    }
  };

  const headerTitle = resolveActiveCustomerContext(session).customerName;

  useEffect(() => {
    let role = '';
    try {
      const s = JSON.parse(localStorage.getItem('session') || 'null');
      role = String(s?.role || localStorage.getItem('userRole') || '').toLowerCase();
    } catch {
      role = String(localStorage.getItem('userRole') || '').toLowerCase();
    }
    if (!role) return;
    if (role !== 'technician') {
      const to =
        role === 'operator'
          ? '/operator'
          : role === 'customer'
          ? '/fleet_dashboard'
          : '/';
      navigate(to, { replace: true });
    }
  }, [navigate]);

  // If technician is linked to >1 customers and no valid active selection exists, force selection screen.
  useEffect(() => {
    const roleLower = String(session?.role || '').toLowerCase();
    if (roleLower && roleLower !== 'technician') return;

    if (!userId) {
      setSelectionReady(true);
      return;
    }

    let cancelled = false;

    const run = async () => {
      try {
        const { data } = await api.get(`/api/users/${userId}/customers`, { skipCustomer: true });
        if (cancelled) return;

        const rows = Array.isArray(data) ? data : [];
        const customers = [];
        for (const r of rows) {
          const id = Number(r.customer_id ?? r.id);
          if (!Number.isFinite(id) || id <= 0) continue;
          const name = r.name ?? r.customerName ?? r.customer_name ?? `Customer #${id}`;
          if (!customers.some((x) => x.id === id)) customers.push({ id, name });
        }

        const count = customers.length;

        if (count > 1) {
          const selectedCid = readSelectedCustomerId();
          const sessionCid = session?.customerId != null ? Number(session.customerId) : null;
          const activeCid = selectedCid || sessionCid || null;

          const activeIsValid =
            activeCid != null && customers.some((c) => c.id === Number(activeCid));

          if (!activeIsValid) {
            try {
              if (sessionCid != null && customers.some((c) => c.id === sessionCid)) {
                localStorage.setItem('pendingCustomerId', String(sessionCid));
              } else {
                localStorage.removeItem('pendingCustomerId');
              }
            } catch {}

            navigate('/select-account', { replace: true });
            return;
          }
        } else if (count === 1) {
          const only = customers[0];
          const selectedCid = readSelectedCustomerId();
          const sessionCid = session?.customerId != null ? Number(session.customerId) : null;
          const activeCid = selectedCid || sessionCid || null;

          if (activeCid == null || Number(activeCid) !== only.id) {
            setActiveCustomerStorage(only.id, only.name);
          }
        }

        setSelectionReady(true);
      } catch {
        if (cancelled) return;
        setSelectionReady(true);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [userId, session?.customerId, session?.role, navigate]);

  const loadSettings = useCallback(async () => {
    if (!customerId) {
      setSettings({
        serviceIntervalType: 'DAYS',
        serviceIntervalValue: 90,
        dueSoonWindow: 30,
        upcomingWindow: 60,
      });
      return;
    }
    try {
      const { data } = await api.get(`/api/customers/${customerId}/maintenance-settings`);
      setSettings({
        serviceIntervalType: data?.serviceIntervalType || 'DAYS',
        serviceIntervalValue: Number(data?.serviceIntervalValue || 90),
        dueSoonWindow: Number(data?.dueSoonWindow || 30),
        upcomingWindow: Number(data?.upcomingWindow || 60),
      });
    } catch {
      setSettings({
        serviceIntervalType: 'DAYS',
        serviceIntervalValue: 90,
        dueSoonWindow: 30,
        upcomingWindow: 60,
      });
    }
  }, [customerId]);

  const loadForklifts = useCallback(async () => {
    try {
      const { data } = await api.get('/api/forklifts');
      setForklifts(Array.isArray(data) ? data : []);
    } catch {
      setForklifts([]);
    }
  }, []);

  const loadMaintSummary = useCallback(async () => {
    try {
      const { data } = await api.get('/api/forklifts/summary');
      setMaintSummary({
        overdueCount: Number(data?.overdueCount || 0),
        dueSoonCount: Number(data?.dueSoonCount || 0),
        upcomingCount: Number(data?.upcomingCount || 0),
      });
    } catch {
      setMaintSummary({
        overdueCount: 0,
        dueSoonCount: 0,
        upcomingCount: 0,
      });
    }
  }, []);

  const loadServiceSummary = useCallback(async () => {
    try {
      const { data } = await api.get('/api/forklifts/service-summary');
      setServiceSummary({
        inServiceCount: Number(data?.inServiceCount || 0),
        downCount: Number(data?.downCount || 0),
        waitingPartsCount: Number(data?.waitingPartsCount || 0),
      });
    } catch {
      setServiceSummary({
        inServiceCount: 0,
        downCount: 0,
        waitingPartsCount: 0,
      });
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadSettings(), loadForklifts(), loadMaintSummary(), loadServiceSummary()]);
    setLoading(false);
  }, [loadSettings, loadForklifts, loadMaintSummary, loadServiceSummary]);

  useEffect(() => {
    if (!selectionReady) return;
    loadAll();
  }, [selectionReady, loadAll]);

  const isHours =
    String(settings.serviceIntervalType || 'DAYS').toUpperCase() === 'HOURS';

  const filtered = useMemo(() => {
    let list = forklifts;

    list = list.filter((f) => (f.status || '').toUpperCase() !== 'RETIRED');

    if (maintFilter !== 'ALL') {
      list = list.filter((f) => safeMaintKey(f.maintenanceStatus) === maintFilter);
    }

    if (serviceFilter !== 'ALL') {
      list = list.filter((f) => (f.status || '').toUpperCase() === serviceFilter);
    }

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((f) => {
        const unitId = (f.unitId || '').toLowerCase();
        const make = (f.make || '').toLowerCase();
        const model = (f.model || '').toLowerCase();
        const serial = (f.serialNumber || '').toLowerCase();
        return (
          unitId.includes(q) ||
          make.includes(q) ||
          model.includes(q) ||
          serial.includes(q)
        );
      });
    }

    return list;
  }, [forklifts, maintFilter, serviceFilter, search]);

  const totalActiveLiftCount = useMemo(
    () => forklifts.filter((f) => String(f.status || '').toUpperCase() !== 'RETIRED').length,
    [forklifts]
  );

  const sorted = useMemo(() => {
    const rank = {
      OVERDUE: 0,
      DUE_SOON: 1,
      ON_TRACK: 2,
    };
    return [...filtered].sort((a, b) => {
      const ra = rank[safeMaintKey(a.maintenanceStatus)] ?? 99;
      const rb = rank[safeMaintKey(b.maintenanceStatus)] ?? 99;
      if (ra !== rb) return ra - rb;
      return String(a.unitId || '').localeCompare(String(b.unitId || ''), undefined, {
        numeric: true,
      });
    });
  }, [filtered]);

  const ds = Number(settings.dueSoonWindow || 30);
  const up = Number(settings.upcomingWindow || 60);
  const unitLabel = isHours ? 'h' : 'd';

  const columns = [
    {
      title: 'Unit ID',
      dataIndex: 'unitId',
      key: 'unitId',
      render: (text, record) =>
        text ? (
          <Button
            type="link"
            className="dashboard-fleet-unit-link"
            onClick={() =>
              navigate(`/technician/maintenance?unitId=${encodeURIComponent(record.unitId)}`)
            }
          >
            {text}
          </Button>
        ) : (
          '—'
        ),
    },
    {
      title: 'Make',
      dataIndex: 'make',
      key: 'make',
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
    },
    {
      title: 'Serial #',
      dataIndex: 'serialNumber',
      key: 'serialNumber',
    },
    {
      title: 'Hours',
      dataIndex: 'hourMeter',
      key: 'hourMeter',
      render: (value) => (value || value === 0 ? value : '—'),
    },
    {
      title: 'Fleet status',
      key: 'service',
      render: (_, r) => {
        const k = String(r.status || '').toUpperCase();
        const label = svcText[k];
        const color = svcColor[k] || 'default';
        return label ? <DashboardStatusTag color={color}>{label}</DashboardStatusTag> : '—';
      },
    },
    {
      title: 'Maintenance',
      key: 'maint',
      render: (_, r) => {
        const k = safeMaintKey(r.maintenanceStatus);
        const label = maintText[k];
        const color = maintColor[k] || 'default';
        return label ? <DashboardStatusTag color={color}>{label}</DashboardStatusTag> : '—';
      },
    },
  ];

  const clearFilters = () => {
    setMaintFilter('ALL');
    setServiceFilter('ALL');
    setSearch('');
  };

  const statusCards = [
    {
      key: 'OVERDUE',
      icon: <WarningOutlined />,
      mobileLabel: 'Overdue',
      label: 'Overdue',
      value: maintSummary.overdueCount,
      helper: 'Past service threshold',
      tone: 'danger',
      active: maintFilter === 'OVERDUE',
      onClick: () => setMaintFilter(maintFilter === 'OVERDUE' ? 'ALL' : 'OVERDUE'),
    },
    {
      key: 'DUE_SOON',
      icon: <ClockCircleOutlined />,
      mobileLabel: 'Due Soon',
      label: `Due soon (0-${ds}${unitLabel})`,
      value: maintSummary.dueSoonCount,
      helper: 'Prioritize scheduling',
      tone: 'warning',
      active: maintFilter === 'DUE_SOON',
      onClick: () => setMaintFilter(maintFilter === 'DUE_SOON' ? 'ALL' : 'DUE_SOON'),
    },
    {
      key: 'UPCOMING',
      icon: <CalendarOutlined />,
      mobileLabel: 'Upcoming',
      label: `Upcoming (${ds + 1}–${up}${unitLabel})`,
      value: maintSummary.upcomingCount,
      helper: 'Plan ahead',
      tone: 'info',
      active: maintFilter === 'UPCOMING',
      onClick: () => setMaintFilter(maintFilter === 'UPCOMING' ? 'ALL' : 'UPCOMING'),
    },
    {
      key: 'DOWN',
      icon: <ToolOutlined />,
      mobileLabel: 'Down',
      label: 'Down',
      value: serviceSummary.downCount,
      helper: 'Out of service',
      tone: 'danger',
      active: serviceFilter === 'DOWN',
      onClick: () => setServiceFilter(serviceFilter === 'DOWN' ? 'ALL' : 'DOWN'),
    },
    {
      key: 'WAITING_ON_PARTS',
      icon: <ClockCircleOutlined />,
      mobileLabel: 'Waiting On Parts',
      label: 'WAITING ON PARTS',
      value: serviceSummary.waitingPartsCount,
      helper: 'Awaiting parts',
      tone: 'warning',
      active: serviceFilter === 'WAITING_ON_PARTS',
      onClick: () =>
        setServiceFilter(serviceFilter === 'WAITING_ON_PARTS' ? 'ALL' : 'WAITING_ON_PARTS'),
    },
    {
      key: 'IN_SERVICE',
      icon: <CheckCircleOutlined />,
      mobileLabel: 'In Service',
      label: 'In service',
      value: serviceSummary.inServiceCount,
      helper: 'Available now',
      tone: 'success',
      active: serviceFilter === 'IN_SERVICE',
      onClick: () => setServiceFilter(serviceFilter === 'IN_SERVICE' ? 'ALL' : 'IN_SERVICE'),
    },
  ];

  const maintenanceStatusCards = statusCards.slice(0, 3);
  const serviceStatusCards = statusCards.slice(3);
  const mobileMaintenanceStatusFilters = maintenanceStatusCards.map(({ key, mobileLabel, label, value, tone, active, onClick }) => ({
    key,
    label: mobileLabel || label,
    value,
    tone,
    active,
    onClick,
  }));
  const mobileHealthStatusFilters = serviceStatusCards.map(({ key, mobileLabel, label, value, tone, active, onClick }) => ({
    key,
    label: mobileLabel || label,
    value,
    tone,
    active,
    onClick,
  }));

  const renderStatusCard = (card) => (
    <DashboardFleetStatusFilterButton
      key={card.key}
      active={card.active}
      icon={card.icon}
      label={card.label}
      mobileLabel={card.mobileLabel}
      value={card.value}
      helper={card.helper}
      tone={card.tone}
      onClick={card.onClick}
    />
  );

  const headerActions = ({ activeCustomerName, logout, navigate, session }) => (
    <DashboardAccountMenu
      customerName={activeCustomerName}
      logout={logout}
      navigate={navigate}
      session={session}
      onOpenChange={setAccountMenuOpen}
      open={accountMenuOpen}
    />
  );

  return (
    <DashboardPageShell
      title={headerTitle}
      contentClassName="dashboard-fleet-page"
      headerClassName="dashboard-fleet-header"
      productDashboardShell="customer"
      renderHeaderActions={headerActions}
    >
      <DashboardPanel className="dashboard-fleet-dashboard-panel">
        <div className="dashboard-fleet-section-heading">
          <div>
            <Title level={3}>Fleet Status</Title>
            <Text type="secondary" className="dashboard-fleet-helper-text">
              Use the status summaries to filter the forklift table below.
            </Text>
          </div>
          <Button onClick={clearFilters}>Clear filters</Button>
        </div>

        <Row gutter={[16, 16]} className="dashboard-fleet-status-row" aria-label="Fleet status filters">
          <Col xs={24} lg={12}>
            <Card className="dashboard-fleet-status-panel" title="Fleet Maintenance Status">
              <div className="dashboard-fleet-status-grid">
                {maintenanceStatusCards.map(renderStatusCard)}
              </div>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card className="dashboard-fleet-status-panel" title="Fleet Health Status">
              <div className="dashboard-fleet-status-grid">
                {serviceStatusCards.map(renderStatusCard)}
              </div>
            </Card>
          </Col>
        </Row>

        <DashboardFleetStatusFilterGrid
          maintenanceFilters={mobileMaintenanceStatusFilters}
          healthFilters={mobileHealthStatusFilters}
        />

        <div className="dashboard-fleet-table-area">
          <div className="dashboard-fleet-table-toolbar">
            <div className="dashboard-fleet-table-toolbar-copy">
              <Text type="secondary" className="dashboard-fleet-helper-text">
                Select a Unit ID to view service records.
              </Text>
              <Text type="secondary" className="dashboard-fleet-table-count">
                Showing {sorted.length} of {totalActiveLiftCount} active lifts
              </Text>
            </div>
            <Space className="dashboard-fleet-table-controls" wrap>
              <DashboardSearchInput
                placeholder="Search forklifts"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="dashboard-fleet-search"
                ariaLabel="Search forklifts"
              />
              <DashboardRefreshButton onClick={loadAll} loading={loading} className="technician-dashboard-refresh" />
            </Space>
          </div>

          <div className="dashboard-fleet-technician-table-wrap">
            <Table
              dataSource={sorted}
              columns={columns}
              rowKey="id"
              pagination={false}
              size="middle"
              rowClassName={(record) =>
                record.id === selectedRowId ? 'ant-table-row-selected' : ''
              }
              onRow={(record) => ({
                onClick: () => setSelectedRowId(record.id),
              })}
              scroll={{ x: 'max-content' }}
              loading={loading}
              locale={{ emptyText: 'No forklifts found.' }}
            />
          </div>

          <div className="dashboard-fleet-mobile-forklift-list technician-dashboard-mobile-list" aria-label="Forklift list">
            {sorted.length ? (
              sorted.map((forklift) => (
                <DashboardForkliftMobileCard
                  key={forklift.id}
                  forklift={forklift}
                  navigate={navigate}
                  onSelect={() => setSelectedRowId(forklift.id)}
                  selected={forklift.id === selectedRowId}
                  maintenancePathBase="/technician/maintenance"
                  missingUnitLabel="Unassigned"
                />
              ))
            ) : (
              <div className="dashboard-fleet-mobile-empty">
                <Empty description="No forklifts found." image={Empty.PRESENTED_IMAGE_SIMPLE} />
              </div>
            )}
          </div>
        </div>
      </DashboardPanel>
    </DashboardPageShell>
  );
}
